import type { LoomNativeHybridLayers } from "./LoomNativeHybridViewer";
import type { LoomNativeHybridAsset } from "./loomNativeHybridAsset";
import type { LoomViewportPresentationState } from "./loomViewportPresentation";

interface LoomNativeInspectionPanelProps {
  asset: LoomNativeHybridAsset | null;
  jacobianScale: number;
  layers: LoomNativeHybridLayers;
  onJacobianScaleChange: (scale: number) => void;
  onLayerToggle: (name: keyof LoomNativeHybridLayers) => void;
  onParameterChange: (parameterId: string) => void;
  open: boolean;
  parameterId: string;
  sourceFixtureOverlayAvailable: boolean;
  teKuttaPairVerified: boolean;
  viewportPresentation: LoomViewportPresentationState;
}

const LAYER_LABELS: Record<keyof LoomNativeHybridLayers, string> = {
  surface: "Exact surfaces",
  parameterization: "Surface parameterization (u/v)",
  tessellation: "Surface triangulation",
  sharedSeam: "Shared conforming seam",
  orderedPanels: "Cp / provenance panels (not accepted for symmetric display)",
  trailingEdge: "Reflected source-fixture smart TE",
  jacobian: "Reflected frozen-topology dV/dp vectors",
};

export function LoomNativeInspectionPanel({
  asset,
  jacobianScale,
  layers,
  onJacobianScaleChange,
  onLayerToggle,
  onParameterChange,
  open,
  parameterId,
  sourceFixtureOverlayAvailable,
  teKuttaPairVerified,
  viewportPresentation,
}: LoomNativeInspectionPanelProps) {
  const selectedParameter = asset?.header.parameters.definitions.find((definition) => definition.id === parameterId) ?? null;
  return (
    <aside aria-label="Inspector pane" className="pg-panel pg-inspector" data-dock="right" data-layout-mode="desktop-pinned-responsive-overlay" data-open={String(open)} data-testid="loom-pane-inspector" id="loom-inspector-pane">
      <div className="pg-panel-heading"><div><span>Inspect</span><strong>Geometry display</strong></div></div>
      <div className="pg-native-equations">
        <div><span>Implicit predicate</span><code>f = min(f_wing, f_body)</code><small>Chooses material membership and Boolean topology.</small></div>
        <div><span>Explicit geometry</span><code>S_wing(u,v;p), S_body(u,v;p)</code><small>Carries the true surface, feature lines and derivatives.</small></div>
        <div><span>Conforming join</span><code>S_wing = S_body on Γ</code><small>Both trimmed blocks reference the same seam coordinates.</small></div>
      </div>
      <div className="pg-layer-list" aria-label="Geometry evidence layers">
        {(Object.keys(LAYER_LABELS) as Array<keyof LoomNativeHybridLayers>).map((name) => {
          const sourceOnly = (name === "trailingEdge" || name === "jacobian") && !sourceFixtureOverlayAvailable;
          const unavailable = name === "orderedPanels" || sourceOnly;
          const suppressed = viewportPresentation.assemblyView !== "assembled" && name !== "surface" && name !== "tessellation" && layers[name];
          const label = name === "trailingEdge" && !teKuttaPairVerified
            ? "Reflected source-fixture TE candidate"
            : LAYER_LABELS[name];
          return (
            <button aria-pressed={unavailable ? false : layers[name]} data-testid={`loom-layer-${name}`} disabled={unavailable} key={name} onClick={() => onLayerToggle(name)} type="button">
              <span>{label}</span>
              <strong>{name === "orderedPanels" ? "unavailable" : sourceOnly ? "base only" : suppressed ? "suppressed by view" : layers[name] ? "shown" : "hidden"}</strong>
            </button>
          );
        })}
      </div>
      <div className="pg-model-picker">
        <label htmlFor="loom-jacobian-parameter">Derivative parameter</label>
        <select data-testid="loom-jacobian-parameter" disabled={!sourceFixtureOverlayAvailable} id="loom-jacobian-parameter" onChange={(event) => onParameterChange(event.target.value)} value={parameterId}>
          {(asset?.header.parameters.definitions ?? []).map((parameter) => <option key={parameter.id} value={parameter.id}>{parameter.label}</option>)}
        </select>
        <span>{selectedParameter ? `p = ${selectedParameter.value} ${selectedParameter.displayUnit} · dV/dp in ${selectedParameter.jacobianUnit}` : "Loading analytic derivative contract…"}</span>
      </div>
      <label className="pg-control pg-native-vector-scale">
        <span><b>dV/dp display scale</b><output>{jacobianScale.toFixed(2)}×</output></span>
        <input aria-label="Derivative vector display scale" data-testid="loom-jacobian-scale" disabled={!sourceFixtureOverlayAvailable} max={0.8} min={0.02} onChange={(event) => onJacobianScaleChange(Number(event.target.value))} step={0.02} type="range" value={jacobianScale} />
      </label>
      <div className="pg-definition-note"><strong>Frozen-topology derivative contract</strong><span>Analytic dV/dp is exact while vertex identity and seam topology remain fixed. A topology change triggers a rebuild.</span></div>
    </aside>
  );
}
